import {
  eyeIcon,
  save as saveIcon,
  LibraryIcon,
  PlusIcon,
  CenterHorizontallyIcon,
} from "@excalidraw/excalidraw/components/icons";
import { MainMenu, CaptureUpdateAction } from "@excalidraw/excalidraw/index";
import React, { useState, useEffect, useCallback } from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { restoreElements } from "@excalidraw/excalidraw/data/restore";

import { LanguageList } from "../app-language/LanguageList";

import { saveDebugState } from "./DebugCanvas";
import {
  saveToCloud,
  updateOnCloud,
  getCloudId,
  clearCloudId,
  DiagramGallery,
} from "./CloudStorage";

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  setTheme: (theme: Theme | "system") => void;
  refresh: () => void;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
}> = React.memo((props) => {
  const [showGallery, setShowGallery] = useState(false);

  const handleSave = useCallback(async () => {
    if (!props.excalidrawAPI) return;
    const elements = props.excalidrawAPI.getSceneElements();
    const appState = props.excalidrawAPI.getAppState();
    const cloudId = getCloudId();

    if (cloudId) {
      const ok = await updateOnCloud(cloudId, elements, appState);
      if (ok) {
        alert("Diagramme sauvegarde !");
      } else {
        alert("Erreur lors de la sauvegarde");
      }
    } else {
      const result = await saveToCloud(elements, appState);
      if (result) {
        alert(`"${result.name}" sauvegarde !`);
      }
    }
  }, [props.excalidrawAPI]);

  const handleAutoAlign = useCallback(() => {
    if (!props.excalidrawAPI) return;
    const allElements = props.excalidrawAPI.getSceneElements();
    const appState = props.excalidrawAPI.getAppState();

    // Only consider "primary" elements: shapes, not bound text or arrows
    const isShape = (el: any) =>
      !el.isDeleted &&
      !el.containerId &&
      el.type !== "arrow" &&
      el.type !== "line";

    const selectedIds = Object.keys(appState.selectedElementIds || {});
    // If selection has 2+ shapes, use those; otherwise use all shapes
    let targets = selectedIds.length > 1
      ? allElements.filter((el: any) => selectedIds.includes(el.id) && isShape(el))
      : allElements.filter(isShape);

    if (targets.length < 2) {
      alert("Selectionne au moins 2 formes pour l'auto-alignement.");
      return;
    }

    // Group elements into rows (elements within 60px of each other vertically)
    const sorted = [...targets].sort((a: any, b: any) => a.y - b.y);
    const rows: any[][] = [];
    let currentRow: any[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const prevMidY = currentRow.reduce((s: number, e: any) => s + e.y + e.height / 2, 0) / currentRow.length;
      const curMidY = sorted[i].y + sorted[i].height / 2;
      if (Math.abs(curMidY - prevMidY) < 60) {
        currentRow.push(sorted[i]);
      } else {
        rows.push(currentRow);
        currentRow = [sorted[i]];
      }
    }
    rows.push(currentRow);

    // Find the global center X
    const allMinX = Math.min(...targets.map((el: any) => el.x));
    const allMaxX = Math.max(...targets.map((el: any) => el.x + el.width));
    const centerX = (allMinX + allMaxX) / 2;

    // Build delta map (dx, dy per shape id)
    const deltas: Record<string, { dx: number; dy: number }> = {};
    const gap = 24;

    for (const row of rows) {
      const rowSorted = [...row].sort((a: any, b: any) => a.x - b.x);
      const totalWidth = rowSorted.reduce((sum: number, el: any) => sum + el.width, 0);
      const rowWidth = totalWidth + gap * (rowSorted.length - 1);
      let posX = centerX - rowWidth / 2;

      // Align row to average Y
      const avgY = rowSorted.reduce((s: number, el: any) => s + el.y + el.height / 2, 0) / rowSorted.length;

      for (const el of rowSorted) {
        deltas[el.id] = {
          dx: posX - el.x,
          dy: (avgY - el.height / 2) - el.y,
        };
        posX += el.width + gap;
      }
    }

    // Distribute rows vertically with even spacing
    if (rows.length > 1) {
      const rowGap = 36;
      const rowHeights = rows.map(row =>
        Math.max(...row.map((el: any) => el.height))
      );
      const totalRowHeight = rowHeights.reduce((a, b) => a + b, 0) + rowGap * (rows.length - 1);
      const allMinY = Math.min(...targets.map((el: any) => el.y));
      const allMaxY = Math.max(...targets.map((el: any) => el.y + el.height));
      const globalCenterY = (allMinY + allMaxY) / 2;
      let startY = globalCenterY - totalRowHeight / 2;

      for (let r = 0; r < rows.length; r++) {
        const rowCenterY = startY + rowHeights[r] / 2;
        for (const el of rows[r]) {
          if (deltas[el.id]) {
            deltas[el.id].dy = (rowCenterY - el.height / 2) - el.y;
          }
        }
        startY += rowHeights[r] + rowGap;
      }
    }

    // Apply deltas: move shapes, bound text follows via delta, arrows update endpoints
    const updatedElements = allElements.map((el: any) => {
      // Shape that we're aligning
      if (deltas[el.id]) {
        return {
          ...el,
          x: el.x + deltas[el.id].dx,
          y: el.y + deltas[el.id].dy,
        };
      }

      // Bound text inside a shape - apply same delta as parent
      if (el.containerId && deltas[el.containerId]) {
        const d = deltas[el.containerId];
        return { ...el, x: el.x + d.dx, y: el.y + d.dy };
      }

      // Arrows with bindings - update start/end points to follow bound shapes
      if ((el.type === "arrow" || el.type === "line") && el.points) {
        const startBinding = el.startBinding;
        const endBinding = el.endBinding;
        const startDelta = startBinding ? deltas[startBinding.elementId] : null;
        const endDelta = endBinding ? deltas[endBinding.elementId] : null;

        if (startDelta || endDelta) {
          const sdx = startDelta?.dx || 0;
          const sdy = startDelta?.dy || 0;
          const edx = endDelta?.dx || 0;
          const edy = endDelta?.dy || 0;

          // If both ends move by the same amount, just translate the arrow
          if (sdx === edx && sdy === edy) {
            return { ...el, x: el.x + sdx, y: el.y + sdy };
          }

          // Otherwise, move the arrow origin by start delta and adjust end point
          const newPoints = el.points.map((pt: number[], i: number) => {
            if (i === 0) return pt; // origin stays at [0,0]
            // Last point adjusts for the difference between end and start deltas
            if (i === el.points.length - 1) {
              return [pt[0] + (edx - sdx), pt[1] + (edy - sdy)];
            }
            return pt;
          });

          return {
            ...el,
            x: el.x + sdx,
            y: el.y + sdy,
            points: newPoints,
          };
        }
      }

      return el;
    });

    props.excalidrawAPI.updateScene({
      elements: updatedElements,
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  }, [props.excalidrawAPI]);

  const handleNew = useCallback(() => {
    if (!props.excalidrawAPI) return;
    if (!confirm("Creer un nouveau diagramme ? Le diagramme actuel sera conserve dans 'Mes diagrammes' s'il a ete sauvegarde.")) return;
    props.excalidrawAPI.resetScene();
    clearCloudId();
    document.title = "Impulsion Diagrams";
  }, [props.excalidrawAPI]);

  const handleLoadFromGallery = useCallback((data: {
    elements: any[];
    appState: any;
  }) => {
    if (!props.excalidrawAPI) return;
    // Restore elements to add missing fields (seed, version, etc.)
    const restoredElements = restoreElements(data.elements, null, {
      refreshDimensions: true,
      repairBindings: true,
    });
    props.excalidrawAPI.resetScene();
    props.excalidrawAPI.updateScene({
      elements: restoredElements,
      appState: data.appState?.viewBackgroundColor
        ? { viewBackgroundColor: data.appState.viewBackgroundColor }
        : undefined,
    });
    props.excalidrawAPI.scrollToContent(restoredElements, { fitToContent: true });
  }, [props.excalidrawAPI]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ctrl+S / Cmd+S = Sauvegarder
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }
      // Ctrl+N / Cmd+N = Nouveau diagramme (only with Shift to avoid browser conflict)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "N") {
        e.preventDefault();
        handleNew();
      }
      // Ctrl+O / Cmd+O = Mes diagrammes (only with Shift)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "O") {
        e.preventDefault();
        setShowGallery(true);
      }
      // Ctrl+Shift+A = Auto-align
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "A") {
        e.preventDefault();
        handleAutoAlign();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSave, handleNew, handleAutoAlign]);

  return (
    <>
      <MainMenu>
        <MainMenu.Item icon={PlusIcon} onSelect={handleNew}>
          Nouveau diagramme
        </MainMenu.Item>
        <MainMenu.Item icon={saveIcon} onSelect={handleSave}>
          Sauvegarder
        </MainMenu.Item>
        <MainMenu.Item
          icon={LibraryIcon}
          onSelect={() => setShowGallery(true)}
        >
          Mes diagrammes
        </MainMenu.Item>
        <MainMenu.Item icon={CenterHorizontallyIcon} onSelect={handleAutoAlign}>
          Auto-align
        </MainMenu.Item>
        <MainMenu.Separator />
        <MainMenu.DefaultItems.LoadScene />
        <MainMenu.DefaultItems.SaveToActiveFile />
        <MainMenu.DefaultItems.Export />
        <MainMenu.DefaultItems.SaveAsImage />
        {props.isCollabEnabled && (
          <MainMenu.DefaultItems.LiveCollaborationTrigger
            isCollaborating={props.isCollaborating}
            onSelect={() => props.onCollabDialogOpen()}
          />
        )}
        <MainMenu.DefaultItems.CommandPalette className="highlighted" />
        <MainMenu.DefaultItems.SearchMenu />
        <MainMenu.DefaultItems.Help />
        <MainMenu.DefaultItems.ClearCanvas />
        <MainMenu.Separator />
        {isDevEnv() && (
          <MainMenu.Item
            icon={eyeIcon}
            onSelect={() => {
              if (window.visualDebug) {
                delete window.visualDebug;
                saveDebugState({ enabled: false });
              } else {
                window.visualDebug = { data: [] };
                saveDebugState({ enabled: true });
              }
              props?.refresh();
            }}
          >
            Visual Debug
          </MainMenu.Item>
        )}
        <MainMenu.DefaultItems.Preferences />
        <MainMenu.DefaultItems.ToggleTheme
          allowSystemTheme
          theme={props.theme}
          onSelect={props.setTheme}
        />
        <MainMenu.ItemCustom>
          <LanguageList style={{ width: "100%" }} />
        </MainMenu.ItemCustom>
        <MainMenu.DefaultItems.ChangeCanvasBackground />
      </MainMenu>

      {showGallery && (
        <DiagramGallery
          onLoad={handleLoadFromGallery}
          onClose={() => setShowGallery(false)}
        />
      )}
    </>
  );
});
